import { BudgetItemType } from "./budget/budget";
import { TabActive } from "./project.type";

export type MappingSource = "context" | "arrayItem";
export type MappingType = "cell" | "column";
export type FieldDomain = "project" | "budget" | "budgetService" | "rsvp";

export type MappingValueStage = "BUDGET" | "NEGOTIATION" | "APPROVED";

export const MAPPING_VALUE_STAGE_LABEL: Record<MappingValueStage, string> = {
  BUDGET: "Orçamento",
  NEGOTIATION: "Negociação",
  APPROVED: "Fechamento",
};

/** So os valores do servico mudam de etapa; fornecedor e agrupamento nao. */
export function fieldHasStageValues(path: string | undefined): boolean {
  return !!path?.startsWith("budgetValues.");
}

export interface FieldMapping {
  ourField: string;
  source: MappingSource;
  type: MappingType;
  sheetName: string;
  cellRef?: string;
  columnHeader?: string;
  /**
   * Letra da coluna, para quando o texto do cabeçalho se repete na linha — no
   * PME do SOMOS, "Qtdd" está em E, J e O. Sem ela vale a última ocorrência.
   */
  columnLetter?: string;
  /**
   * De qual etapa saem os valores (`budgetValues.*`) desta coluna. O PME do
   * SOMOS tem "Orçamento inicial", "Orçamento atualizado" e "Valor final" lado
   * a lado. Sem ela, vale a etapa da aba de onde se exporta.
   */
  valueStage?: MappingValueStage;
  arrayField?: string;
  headerRow?: number;
  startRow?: number;
  label?: string;
  /** Budget cost category scope (e.g. AEB, AIR) when the field requires category filtering */
  category?: BudgetItemType;
  /**
   * Nome do agrupamento (BudgetGroup.name) que recorta o bloco. Necessário
   * quando o template tem uma seção por agrupamento dentro da mesma categoria —
   * sem ele todas as seções recebem os mesmos serviços.
   */
  group?: string;
}

export interface SpreadsheetCategory {
  categoryKey: BudgetItemType;
  categoryLabel: string;
}

/** As formas de cobrança em que um agrupamento do template pode ser criado. */
export const TEMPLATE_CALC_OPTIONS = ["A", "B", "C", "D"] as const;

/**
 * Uma seção da planilha padrão do cliente. Um orçamento novo desse cliente já
 * nasce com um agrupamento por (entrada, forma de cobrança).
 *
 * O `name` precisa ser o nome EXATO da seção na aba de custos: é por ele que
 * `FieldMapping.group` recorta o bloco na hora de gerar.
 */
export interface BudgetGroupTemplateEntry {
  name: string;
  calcOptions: string[];
}

export interface ClientSpreadsheetRecord {
  _id: string;
  clientId: string;
  s3Key: string;
  originalFileName: string;
  fieldMappings: FieldMapping[];
  budgetGroupTemplate?: BudgetGroupTemplateEntry[];
  createdAt: string;
  updatedAt: string;
}

export interface AvailableField {
  path: string;
  label: string;
  domain: FieldDomain;
  source: MappingSource;
  arrayField?: string;
  isArray?: boolean;
  format?: "text" | "date" | "boolean" | "status";
  requiresCategory?: boolean;
}

export interface SheetHeader {
  column: number;
  letter: string;
  value: string;
}

/**
 * Uma seção que o próprio template declara: o título e o bloco de itens que a
 * fórmula de total dela soma.
 */
export interface TemplateSection {
  name: string;
  headerRow: number;
  startRow: number;
  endRow: number;
}

/**
 * Uma lista suspensa da aba, com as opções. Na planilha sem seções (a da
 * Honda), os agrupamentos pré-estabelecidos são as opções da coluna GRUPO.
 */
export interface TemplateDropdown {
  column: string;
  header: string | null;
  startRow: number;
  endRow: number;
  /** Como está na validação: `Planilha2!$B$2:$B$62` ou `"A,B"`. */
  source: string;
  values: string[];
}

export interface SheetInspection {
  name: string;
  headerRow: number;
  headers: SheetHeader[];
  /** Ausente em resposta de backend antigo. */
  sections?: TemplateSection[];
  /** Ausente em resposta de backend antigo. */
  dropdowns?: TemplateDropdown[];
}

export interface MappingValidationItem {
  index: number;
  ourField: string;
  valid: boolean;
  message?: string;
}

/**
 * As duas listas — agrupamentos semeados e agrupamentos recortados pelos
 * mapeamentos — são independentes, e quando divergem nada quebra visivelmente:
 * o bloco só sai vazio na planilha. Por isso o backend as cruza.
 */
export interface BudgetGroupTemplateValidation {
  /** No template, mas nenhum mapeamento preenche: a seção nasce e fica vazia. */
  groupsWithoutMapping: string[];
  /** Num mapeamento, mas fora do template: só enche se criarem o agrupamento à mão. */
  groupsWithoutTemplateEntry: string[];
}

export interface TemplateInspection {
  sheets: SheetInspection[];
  mappingValidation?: MappingValidationItem[];
  budgetGroupTemplateValidation?: BudgetGroupTemplateValidation;
}

export interface UploadTemplateResponse {
  record: ClientSpreadsheetRecord;
  inspection: TemplateInspection;
  invalidMappingsRemoved: number;
}

export interface SpreadsheetWarning {
  mappingIndex: number;
  ourField: string;
  message: string;
}

export interface GeneratePreviewResponse {
  filename: string;
  filledMappings: number;
  warnings: SpreadsheetWarning[];
}

/** Aba de onde a planilha do cliente foi pedida (espelha `SpreadsheetStage` da Api). */
export type ClientSpreadsheetStage = "BUDGET" | "NEGOTIATION" | "APPROVED";

export interface GenerateSpreadsheetRequest {
  projectId: string;
  budgetIds?: string[];
  strict?: boolean;
  /** NEGOTIATION/APPROVED: a Api preenche com os valores da etapa. */
  stage?: ClientSpreadsheetStage;
  /** Obrigatoria com stage NEGOTIATION. */
  negotiationVersion?: number;
}

/**
 * Etapa a pedir para a planilha do cliente a partir da aba aberta.
 *
 * Sem isto a Api preenchia sempre com os valores do Orcamento: na BRF, o apt
 * single negociado de 700 para 600 saia com 700 na Negociacao e no Fechamento.
 */
export function clientSpreadsheetStageFor(
  tabActive: TabActive | undefined,
  negotiationVersion?: number
): Pick<GenerateSpreadsheetRequest, "stage" | "negotiationVersion"> {
  if (tabActive === TabActive.NEGOTIATION) {
    return { stage: "NEGOTIATION", negotiationVersion };
  }
  if (tabActive === TabActive.APPROVED) {
    return { stage: "APPROVED" };
  }
  return { stage: "BUDGET" };
}

/** Compatibilidade: respostas legadas da API podem usar categoria */
export type FieldMappingPayload = FieldMapping & { categoria?: BudgetItemType };

export function normalizeFieldMapping(
  mapping: FieldMappingPayload
): FieldMapping;
export function normalizeFieldMapping(
  mapping: Partial<FieldMappingPayload>
): Partial<FieldMapping>;
export function normalizeFieldMapping(
  mapping: Partial<FieldMappingPayload>
): Partial<FieldMapping> {
  const { categoria, ...rest } = mapping;
  return {
    ...rest,
    category: rest.category ?? categoria,
  };
}

/**
 * "Agrupamento do Servico" filtrado pelo PROPRIO agrupamento nao escreve nada:
 * todo servico do recorte tem o mesmo agrupamento — o filtro — e o titulo da
 * secao ja esta no template, entao a geracao pula o campo.
 *
 * Espelha `repeatsGroupFilter` de `mapping-apply.util.ts` na Api. Se as duas
 * divergirem, a tela volta a salvar em silencio um mapeamento que a planilha
 * ignora — foi assim que o QA gerou o bloco "3 - INSTAGRAMÁVEL" vazio sem
 * entender por que.
 */
export function repeatsGroupFilter(
  mapping: Partial<Pick<FieldMapping, "source" | "ourField" | "group">>
): boolean {
  return (
    mapping.source === "arrayItem" &&
    mapping.ourField === "groupName" &&
    Boolean(mapping.group?.trim())
  );
}

export const REPEATS_GROUP_FILTER_MESSAGE =
  "Agrupamento do Servico filtrado pelo proprio agrupamento nao escreve nada na planilha: todos os servicos do recorte tem esse agrupamento, e o titulo da secao ja esta no template. Para preencher o bloco, mapeie as colunas dos servicos (Tipo de Servico, Quantidade, Diarias, Valor Unitario...).";

/** Quantos avisos cabem na notificacao antes de resumir o resto. */
const MAX_WARNINGS_IN_NOTIFICATION = 2;

/**
 * Notificacao depois de exportar pela planilha do cliente.
 *
 * Cada aviso da Api diz o que ficou de fora e como resolver. Resumir tudo em
 * "Alguns campos podem estar vazios" deixava o usuario sem saber o que
 * configurar — a planilha saia vazia e nada na tela dizia por que.
 */
export function describeClientSpreadsheetExport(meta: {
  warnings: SpreadsheetWarning[];
  filledMappings: number;
}): { message: string; severity: "success" | "warning" } {
  const warnings = meta.warnings ?? [];

  if (!warnings.length) {
    return {
      message: `Exportado com sucesso! ${meta.filledMappings} campo(s) preenchido(s).`,
      severity: "success",
    };
  }

  const shown = warnings
    .slice(0, MAX_WARNINGS_IN_NOTIFICATION)
    .map((warning) => warning.message)
    .join(" | ");
  const hidden = warnings.length - MAX_WARNINGS_IN_NOTIFICATION;

  return {
    message: `Exportado com ${warnings.length} aviso(s): ${shown}${
      hidden > 0 ? ` (e mais ${hidden})` : ""
    }`,
    severity: "warning",
  };
}
